import "./MapView.scss"
import iconMapZoomIn from "assets/img/icon-map-zoom-in.svg"
import iconMapZoomOut from "assets/img/icon-map-zoom-out.svg"
import iconMapZoomToFit from "assets/img/icon-map-zoom-to-fit.svg"
import iconMapFullscreen from "assets/img/icon-map-fullscreen.svg"
import iconMapMinimize from "assets/img/icon-map-minimize.svg"
import mapTypeRoadmap from "assets/img/map-type-roadmap.png"
import mapTypeSatellite from "assets/img/map-type-satellite.png"

import React, { FunctionComponent, useCallback, useState, memo, useEffect, useMemo } from "react"
import { GoogleMap, useLoadScript } from "@react-google-maps/api"
import { useUnit } from "effector-react"
import i18n from "intl/i18n"
import cx from "classnames"

import config from "config/config"
import { Flex } from "components/Flex"
import Spinner from "components/Spinner/Spinner"
import MapMarker, { IMapMarkerProps } from "components/MapView/MapMarker/MapMarker"
import MapCircle, { IMapCircleProps } from "./MapCircle/MapCircle"
import MapClusterer from "components/MapView/MapClusterer/MapClusterer"
import { Coordinates } from "@/types"
import LocationService from "services/LocationService"
import { MapType } from "config/constants"
import { getZIndex } from "utils/modal"
import modals from "stores/modals"

export interface IMapElProps {
    mapType: MapType
    mapZoom: number
    onZoomChanged?(): void
    onLoad?(map: google.maps.Map): void
    markers?: IMapMarkerProps[]
    circles?: IMapCircleProps[]
    onMapClick?(coordinates: Coordinates): void
    preventInfoWindowsClose?: boolean
}

const MapEl = memo(({
    mapType,
    mapZoom,
    onZoomChanged,
    onLoad,
    markers = [],
    circles = [],
    onMapClick,
    preventInfoWindowsClose,
}: IMapElProps) => {
    const [clusterPopover, setClusterPopover] = useState<React.JSX.Element>()

    const markersClusterable = useMemo(() => {
        return markers.filter(marker => marker.isClusterable)
            .map((marker, index) => {
                marker.index = index + 1
                return marker
            })
    }, [markers])
    const markersNonClusterable = useMemo(() => {
        return markers.filter(marker => !marker.isClusterable)
    }, [markers])

    useEffect(() => {
        setClusterPopover(undefined)
    }, [mapZoom])

    function handleMapClick(e: google.maps.MapMouseEvent) {
        if (typeof onMapClick === "function" && e.latLng) {
            onMapClick({
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
            })
        }
    }

    return (
        <GoogleMap
            options={{
                disableDefaultUI: true,
                scaleControl: true,
                mapTypeId: mapType,
                zoom: mapZoom,
            }}
            onZoomChanged={onZoomChanged}
            onLoad={onLoad}
            onClick={handleMapClick}>
            <MapClusterer
                markers={markersClusterable}
                mapType={mapType}
                updateClusterPopover={setClusterPopover}
                preventInfoWindowsClose={preventInfoWindowsClose} />
            {markersNonClusterable.map((marker, index) => (
                <MapMarker
                    key={index}
                    {...marker} />
            ))}
            {circles.map((circle, index) => (
                <MapCircle
                    key={index.toString()}
                    {...circle} />
            ))}
            {clusterPopover}
        </GoogleMap>
    )
})
MapEl.displayName = "MapEl"

export enum MapViewControl {
    zoom = "zoom",
    zoomToFit = "zoomToFit",
    fullscreen = "fullscreen",
    type = "type",
}

interface CustomMapControl {
    position: "right-bottom"
    title?: string
    icon: string
    onClick: () => void
}

export interface IMapViewProps {
    markers?: IMapMarkerProps[],
    circles?: IMapCircleProps[],
    info?: React.JSX.Element | string | null,
    autoCenterToMarkers?: boolean,
    autoCenterToUserLocation?: boolean,
    initCenter?: Coordinates
    onMapClick?(coordinates: Coordinates): void,
    preventInfoWindowsClose?: boolean
    controls?: (MapViewControl | CustomMapControl)[]
    fullscreen?: boolean
    onFullscreenExit?(): void
}

const MapView: FunctionComponent<React.PropsWithChildren<IMapViewProps>> = ({
    markers,
    circles,
    info,
    autoCenterToMarkers,
    autoCenterToUserLocation,
    initCenter,
    onMapClick,
    preventInfoWindowsClose,
    controls,
    fullscreen: initFullscreen,
    onFullscreenExit,
}) => {
    const [map, setMap] = useState<google.maps.Map | null>(null)
    const [mapZoom, setMapZoom] = useState(1)
    const [mapType, setMapType] = useState<MapType>(MapType.roadmap)
    const [fullscreen, setFullscreen] = useState(false)
    const [zIndex, setZIndex] = useState(0)
    const modalZIndexes = useUnit(modals.zIndexes)
    const [hasBeenCentered, setHasBeenCentered] = useState(false)

    const { isLoaded: isMapScriptLoaded } = useLoadScript({
        googleMapsApiKey: config.googleMapsApiKey || "",
        language: i18n.language
    })

    const centerMapToCoordinates = useCallback(async (theMap: google.maps.Map, coordinates: Coordinates) => {
        theMap.setCenter(coordinates)
        theMap.setZoom(13)
        setHasBeenCentered(true)
    }, [])

    const centerMapToWorld = useCallback(async (theMap: google.maps.Map) => {
        const bounds = new google.maps.LatLngBounds()
        bounds.extend({ lat: -20, lng: -90 })
        bounds.extend({ lat: 70, lng: 90 })
        theMap.fitBounds(bounds)
    }, [])

    const centerMapToMarkers = useCallback(async (theMap: google.maps.Map) => {
        if (!markers || markers.length === 0) {
            centerMapToWorld(theMap)
            return
        }

        if (markers.length === 1) {
            centerMapToCoordinates(theMap, markers[0].coordinates)
            return
        }

        const bounds = new google.maps.LatLngBounds()
        for (const marker of markers) {
            bounds.extend(marker.coordinates)
        }

        theMap.fitBounds(bounds)
        setHasBeenCentered(true)
    }, [markers, centerMapToWorld, centerMapToCoordinates])

    const centerMapToUser = useCallback(async (theMap: google.maps.Map) => {
        const userLocation = await LocationService.getCurrentLocation()
        if (userLocation) {
            await centerMapToCoordinates(theMap, userLocation)
        } else {
            centerMapToWorld(theMap)
        }
    }, [centerMapToCoordinates, centerMapToWorld])


    useEffect(() => {
        if (!map || !autoCenterToMarkers) return
        centerMapToMarkers(map)
    }, [autoCenterToMarkers, centerMapToMarkers, map, markers])

    const initCenterLat = initCenter?.lat
    const initCenterLng = initCenter?.lng

    useEffect(() => {
        if (!map || autoCenterToMarkers) return

        if (initCenter) {
            centerMapToCoordinates(map, initCenter)
        } else if (autoCenterToUserLocation) {
            centerMapToUser(map)
        } else {
            if (!hasBeenCentered) {
                centerMapToWorld(map)
            }
        }
    }, [map, initCenterLat, initCenterLng, autoCenterToMarkers, autoCenterToUserLocation, initCenter, centerMapToCoordinates, centerMapToUser, centerMapToWorld, hasBeenCentered])

    function onZoomChanged() {
        const zoom = map?.getZoom()
        if (typeof zoom === "number") {
            setMapZoom(zoom)
        }
    }

    function zoomIn() {
        if (mapZoom <= 21) {
            setMapZoom(mapZoom + 1)
        }
    }

    function zoomOut() {
        if (mapZoom >= 1) {
            setMapZoom(mapZoom - 1)
        }
    }

    function toggleMapType() {
        if (mapType === MapType.roadmap) {
            setMapType(MapType.hybrid)
            return
        }
        setMapType(MapType.roadmap)
    }

    function toggleFullscreen() {
        setFullscreen(currentFullscreen => {
            if (currentFullscreen) {
                modals.removeZIndex(zIndex)
                if (typeof onFullscreenExit === "function") {
                    onFullscreenExit()
                }
            } else {
                const newZIndex = getZIndex()
                setZIndex(newZIndex)
                modals.addZIndex(newZIndex)
            }
            return !currentFullscreen
        })
    }

    useEffect(() => {
        if (initFullscreen) {
            toggleFullscreen()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initFullscreen])

    const handleKeydown = useCallback((e: KeyboardEvent) => {
        if (e.key !== "Escape" || modalZIndexes[modalZIndexes.length - 1] !== zIndex) return
        setFullscreen(false)
        modals.removeZIndex(zIndex)
    }, [modalZIndexes, zIndex])

    useEffect(() => {
        if (fullscreen) {
            document.addEventListener("keydown", handleKeydown)
        } else {
            document.removeEventListener("keydown", handleKeydown)
        }
        return () => document.removeEventListener("keydown", handleKeydown)
    }, [fullscreen, handleKeydown])

    return (
        <div className={cx([
            "map-view",
            { "map-view--fullscreen": fullscreen },
        ])}
            style={fullscreen ? { zIndex } : {}}
            data-testid="map">
            <div className="map-view__content">
                {!markers &&
                    <Flex className="map-view__loader"
                        alignItems="center"
                        justifyContent="center">
                        <Spinner />
                    </Flex>
                }
                {isMapScriptLoaded &&
                    <>
                        {info &&
                            <div className="map-view__info">
                                {info}
                            </div>
                        }
                        {controls?.includes(MapViewControl.type) &&
                            <button
                                type="button"
                                className="map-view__map-type-control"
                                aria-label="Switch map type"
                                onClick={toggleMapType}>
                                {mapType === MapType.roadmap &&
                                    <img src={mapTypeSatellite} alt="" />
                                }
                                {mapType === MapType.hybrid &&
                                    <img src={mapTypeRoadmap} alt="" />
                                }
                            </button>
                        }
                        {(initFullscreen
                            || controls?.includes(MapViewControl.fullscreen)
                            || controls?.includes(MapViewControl.zoom)
                            || controls?.includes(MapViewControl.zoomToFit)
                            || controls?.some(c => typeof c === "object" && c.position === "right-bottom")
                        ) &&
                            <div className="map-view__bottom-right-controls">
                                {controls
                                    ? controls
                                        .filter(c => typeof c === "object" && c.position === "right-bottom")
                                        .map((c, cIndex) => {
                                            const control = c as CustomMapControl
                                            return (
                                                <div className="map-view__bottom-right-controls-item"
                                                    key={cIndex}>
                                                    <button
                                                        type="button"
                                                        className="map-view__bottom-right-control"
                                                        onClick={control.onClick}
                                                        aria-label={control.title}>
                                                        <img src={control.icon} alt="" />
                                                    </button>
                                                </div>
                                            )
                                        })
                                    : null
                                }
                                {(initFullscreen || controls?.includes(MapViewControl.fullscreen)) &&
                                    <div className="map-view__bottom-right-controls-item">
                                        <button type="button" className="map-view__bottom-right-control" aria-label="+" onClick={toggleFullscreen}>
                                            <img src={fullscreen ? iconMapMinimize : iconMapFullscreen} alt="" />
                                        </button>
                                    </div>
                                }
                                {(controls?.includes(MapViewControl.zoom) || controls?.includes(MapViewControl.zoomToFit)) &&
                                    <div className="map-view__bottom-right-controls-item">
                                        {controls?.includes(MapViewControl.zoom) &&
                                            <button type="button" className="map-view__bottom-right-control" aria-label="+" onClick={zoomIn}>
                                                <img src={iconMapZoomIn} alt="" />
                                            </button>
                                        }
                                        {controls?.includes(MapViewControl.zoomToFit) &&
                                            <button type="button" className="map-view__bottom-right-control" aria-label="zoom to fit" onClick={() => map && centerMapToMarkers(map)}>
                                                <img src={iconMapZoomToFit} alt="" />
                                            </button>
                                        }
                                        {controls?.includes(MapViewControl.zoom) &&
                                            <button type="button" className="map-view__bottom-right-control" aria-label="-" onClick={zoomOut}>
                                                <img src={iconMapZoomOut} alt="" />
                                            </button>
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </>
                }
                {isMapScriptLoaded &&
                    <MapEl
                        mapType={mapType}
                        mapZoom={mapZoom}
                        onZoomChanged={onZoomChanged}
                        onLoad={setMap}
                        markers={markers}
                        circles={circles}
                        onMapClick={onMapClick}
                        preventInfoWindowsClose={preventInfoWindowsClose}
                    />
                }
            </div>
            {fullscreen &&
                <div className="map-view__fullscreen-overlay" onClick={toggleFullscreen}></div>
            }
        </div>

    )
}

export default memo(MapView)
